import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { templateApi } from '../services/emailApi';
import { useToast } from '../shared/ToastProvider';
import { useUserContext, canWrite } from '../../../context/userContext';
import TemplatePreview from './TemplatePreview';
import './TemplateManagement.css';

const PAGE_LIMIT = 6;

const resolveChannels = (template) => {
  if (Array.isArray(template.channels) && template.channels.length > 0) return template.channels;
  if (typeof template.channels === 'string' && template.channels.trim()) return [template.channels.trim()];
  if (template.channel) return [template.channel];
  return [];
};

const extractPreviewPayload = (response) => {
  const root = response?.data;
  if (!root || typeof root !== 'object') return null;

  // New GET /templates/:id/preview format: { success, data: { html, subject, ... } }
  const newFmt = root?.data;
  if (newFmt?.html && newFmt?.subject) {
    return { htmlPreview: newFmt.html, subject: newFmt.subject, ...newFmt };
  }

  // Legacy POST /templates/preview format
  const preview = root?.preview || root?.data?.preview || root?.data;
  const htmlPreview =
    root?.htmlPreview || root?.data?.htmlPreview || preview?.htmlPreview || preview?.html;
  const subject = preview?.subject || root?.subject;
  if (htmlPreview && subject) return { ...preview, htmlPreview, subject };
  return null;
};

const normalizeListResponse = (response) => {
  const data = response?.data;
  const core = data?.data;
  const paginationPayload = data?.pagination || core?.pagination;

  const normPag = (p) => {
    if (!p) return null;
    const pageIndex = p.pageIndex ?? p.page ?? 1;
    const pageSize = p.pageSize ?? p.limit ?? PAGE_LIMIT;
    const total = p.total ?? 0;
    const totalPages = p.totalPages ?? Math.ceil(total / (pageSize || 1));
    const hasNext = p.hasNext ?? pageIndex * pageSize < total;
    const hasPrev = p.hasPrev ?? pageIndex > 1;
    return { page: pageIndex, limit: pageSize, total, totalPages, hasNext, hasPrev };
  };

  /* ── normalise each template so the UI always has consistent keys ── */
  const normalizeTemplate = (t) => ({
    ...t,
    templateName: t.templateName || t.template_name || t.name || '',
    // ALWAYS prefer template_uuid over integer id for all API calls
    templateUuid: t.template_uuid || t.templateUuid || t.id,
    isActive: t.isActive ?? (t.is_active != null ? t.is_active : t.status === 'active'),
    updatedAt: t.updatedAt || t.updated_at || '',
    createdAt: t.createdAt || t.created_at || '',
  });

  if (Array.isArray(core)) return { templates: core.map(normalizeTemplate), pagination: normPag(paginationPayload) };
  if (core?.items) return { templates: core.items.map(normalizeTemplate), pagination: normPag(core.pagination || paginationPayload) };
  if (core) return { templates: [normalizeTemplate(core)], pagination: normPag(paginationPayload) };
  return { templates: [], pagination: normPag(paginationPayload) };
};

const EmailTemplates = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { userCtx } = useUserContext();
  const canWriteTemplates = !userCtx || !userCtx.pages || canWrite(userCtx, 'email_templates');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1, limit: PAGE_LIMIT, total: 0, totalPages: 0, hasNext: false, hasPrev: false
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterChannel, setFilterChannel] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, template: null });

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Dynamic categories fetched from API, with fallback
  const [categories, setCategories] = useState(['all', 'general', 'onboarding', 'transactional', 'marketing']);
  const channels = ['all', 'email', 'whatsapp', 'sms'];

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, searchTerm, filterCategory]);

  // Fetch categories from API on mount
  useEffect(() => {
    templateApi.categories()
      .then((res) => {
        const cats = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(cats) && cats.length > 0) {
          // Normalize: API may return strings or objects like { name: '...' }
          const normalized = cats.map((c) => (typeof c === 'string' ? c : c?.name || c?.category || String(c))).filter(Boolean);
          if (normalized.length > 0) setCategories(['all', ...normalized]);
        }
      })
      .catch(() => { /* keep fallback categories */ });
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: PAGE_LIMIT,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (filterCategory && filterCategory !== 'all') params.category = filterCategory;

      const response = await templateApi.list(params);
      const { templates: fetched, pagination: serverPag } = normalizeListResponse(response);

      setTemplates(fetched.map((t) => ({ ...t })));

      if (serverPag) {
        setPagination({
          page: serverPag.page,
          limit: serverPag.limit,
          total: serverPag.total,
          totalPages: serverPag.totalPages,
          hasNext: serverPag.hasNext ?? serverPag.page < serverPag.totalPages,
          hasPrev: serverPag.hasPrev ?? serverPag.page > 1,
        });
      } else {
        // Fallback: no pagination from server — compute client-side
        const total = fetched.length;
        const totalPages = total > 0 ? Math.ceil(total / PAGE_LIMIT) : 1;
        setPagination({
          page: 1, limit: PAGE_LIMIT, total, totalPages,
          hasNext: false, hasPrev: false,
        });
      }
    } catch (err) {
      console.error('Failed to load templates:', err?.message || err);
      setError('Failed to load templates. Please try again.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (nextPage) => {
    setPagination((prev) => {
      const totalPages = prev.totalPages || 1;
      const clamped = Math.min(Math.max(nextPage, 1), totalPages);
      if (clamped === prev.page) return prev;
      return { ...prev, page: clamped };
    });
  };

  // Reset to page 1 when search or filter changes
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };
  const handleCategoryChange = (value) => {
    setFilterCategory(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCreateNew = () => navigate('/dashboard/templates/new');
  const handleEdit = (template) => {
    const uuid = template.templateUuid || template.id;
    navigate(`/dashboard/templates/${uuid}/edit`);
  };

  const handleDelete = async (templateUuid) => {
    setLoading(true);
    setError(null);
    try {
      await templateApi.remove(templateUuid);
      setSuccess('Template deleted successfully!');
      toast?.success?.('Template deleted');
      const remainingOnPage = templates.length - 1;
      if (remainingOnPage === 0 && pagination.page > 1) {
        setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
      } else {
        await loadTemplates();
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (template) => setDeleteConfirm({ open: true, template });
  const handleDeleteConfirm = () => {
    if (deleteConfirm.template) {
      handleDelete(deleteConfirm.template.templateUuid || deleteConfirm.template.id);
    }
    setDeleteConfirm({ open: false, template: null });
  };
  const handleDeleteCancel = () => setDeleteConfirm({ open: false, template: null });

  const handleDuplicate = async (template) => {
    const templateId = template?.templateUuid || template?.id;
    if (!templateId) return;
    setLoading(true);
    setError(null);
    try {
      const newName = `${template.templateName || template.name || 'Template'} (Copy)`;
      await templateApi.duplicate(templateId, newName);
      toast?.success?.('Template duplicated');
      await loadTemplates();
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError('Failed to duplicate template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (template) => {
    const templateId = template?.templateUuid || template?.id;
    if (!templateId) {
      setError('Preview unavailable: template is not saved yet.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Use new GET endpoint — backend loads template from DB and renders HTML
      const response = await templateApi.previewById(templateId);
      const payload = extractPreviewPayload(response);
      if (payload) {
        setPreviewData(payload);
        setIsPreviewOpen(true);
      } else {
        setError('Preview unavailable: backend did not return preview data.');
      }
    } catch (err) {
      setError('Preview failed. Please try again.');
      console.error('Backend preview failed:', err);
    } finally {
      setLoading(false);
    }
  };

  // Server handles search and category filters; only channel filter is applied client-side
  const filteredTemplates = templates.filter((template) => {
    if (filterChannel === 'all') return true;
    const normalizedChannels = resolveChannels(template);
    return normalizedChannels.includes(filterChannel);
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getBadgeClass = (category) => {
    const map = {
      account: 'em-badge-info', announcements: 'em-badge-warning',
      connections: 'em-badge-primary', engagement: 'em-badge-success',
      events: 'em-badge-success', marketing: 'em-badge-warning',
      messaging: 'em-badge-info', security: 'em-badge-danger',
    };
    return map[category] || 'em-badge-neutral';
  };

  return (
    <div className="emtpl-page">
      {/* Page Header */}
      <div className="emtpl-header">
        <div>
          <h1 className="emtpl-title">Template Management</h1>
          <p className="emtpl-subtitle">
            Create and manage email templates for notifications across multiple channels
          </p>
        </div>
        <button className="em-btn em-btn-primary emtpl-create-btn emtpl-create-btn--header" onClick={handleCreateNew} disabled={loading || !canWriteTemplates}>
          Create Template
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="emtpl-alert emtpl-alert--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
          <span>{error}</span>
          <button className="emtpl-alert-close" onClick={() => setError(null)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {success && (
        <div className="emtpl-alert emtpl-alert--success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
          <span>{success}</span>
          <button className="emtpl-alert-close" onClick={() => setSuccess(null)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Content */}
      {loading && templates.length === 0 ? (
        <div className="emtpl-loading">
          <div className="emtpl-spinner" />
          <p className="emtpl-loading-text">Loading templates...</p>
        </div>
      ) : (
        <div>
          {/* Search & Filters */}
          <div className="emtpl-toolbar">
            <div className="emtpl-toolbar-main">
              <button className="em-btn em-btn-primary emtpl-create-btn emtpl-create-btn--toolbar" onClick={handleCreateNew} disabled={loading || !canWriteTemplates}>
                Create Template
              </button>
              <div className="emtpl-search">
                <svg className="emtpl-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  className="em-input"
                  placeholder="Search by name or subject..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {searchTerm && (
                  <button className="emtpl-search-clear" onClick={() => handleSearchChange('')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
            </div>

            <div className="emtpl-filters">
              <select className="em-select" value={filterCategory} onChange={(e) => handleCategoryChange(e.target.value)}>
                <option value="all">All Categories</option>
                {categories.slice(1).map((cat) => {
                  const label = typeof cat === 'string' ? cat : (cat?.name || cat?.category || String(cat));
                  return <option key={label} value={label}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>;
                })}
              </select>
              <select className="em-select" value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
                <option value="all">All Channels</option>
                {channels.slice(1).map((ch) => (
                  <option key={ch} value={ch}>{ch.charAt(0).toUpperCase() + ch.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="emtpl-results-bar">
            <span>
              {pagination?.total ?? filteredTemplates.length} template{(pagination?.total ?? filteredTemplates.length) !== 1 ? 's' : ''}
            </span>
            {(searchTerm || filterCategory !== 'all' || filterChannel !== 'all') && (
              <button className="emtpl-clear-filters" onClick={() => { handleSearchChange(''); handleCategoryChange('all'); setFilterChannel('all'); }}>
                Clear filters
              </button>
            )}
          </div>

          {/* Empty state */}
          {filteredTemplates.length === 0 ? (
            <div className="em-card">
              <div className="em-empty">
                <div className="em-empty-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <h3>No templates found</h3>
                <p>
                  {searchTerm || filterCategory !== 'all' || filterChannel !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Create your first template to get started'}
                </p>
                {!searchTerm && filterCategory === 'all' && filterChannel === 'all' && canWriteTemplates && (
                  <button className="em-btn em-btn-primary" onClick={handleCreateNew}>Create Template</button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="em-card" style={{ padding: 0 }}>
                <div className="em-table-wrap">
                  <table className="em-table">
                    <thead>
                      <tr>
                        <th>Template</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Channel</th>
                        <th>Subject</th>
                        <th>Updated</th>
                        <th className="emtpl-th-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTemplates.map((template) => {
                        const channelsList = resolveChannels(template);
                        return (
                          <tr key={template.templateUuid || template.id}>
                            <td><span className="emtpl-name">{template.templateName}</span></td>
                            <td>
                              <span className={`em-badge ${getBadgeClass(template.category)}`}>
                                {template.category || '\u2014'}
                              </span>
                            </td>
                            <td>
                              {template.isActive ? (
                                <span className="em-badge em-badge-success"><span className="em-badge-dot" /> Active</span>
                              ) : (
                                <span className="em-badge em-badge-neutral">Inactive</span>
                              )}
                            </td>
                            <td>
                              <span className="emtpl-channel">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                                </svg>
                                {channelsList.length > 0 ? channelsList.join(', ') : 'email'}
                              </span>
                            </td>
                            <td><span className="emtpl-subject">{template.subject || '\u2014'}</span></td>
                            <td><span className="emtpl-date">{formatDate(template.updatedAt)}</span></td>
                            <td>
                              <div className="emtpl-actions">
                                <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => handlePreview(template)} title="Preview">
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                </button>
                                {canWriteTemplates && (
                                <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => handleEdit(template)} title="Edit">
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                </button>
                                )}
                                {canWriteTemplates && (
                                <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => handleDuplicate(template)} title="Duplicate">
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                </button>
                                )}
                                {canWriteTemplates && (
                                <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => handleDeleteClick(template)} title="Delete" style={{ color: 'var(--em-danger)' }}>
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="emtpl-cards-mobile">
                {filteredTemplates.map((template) => {
                  const channelsList = resolveChannels(template);
                  return (
                    <div key={template.templateUuid || template.id} className="emtpl-card-m">
                      <div className="emtpl-card-m-header">
                        <span className="emtpl-name">{template.templateName}</span>
                        <div className="emtpl-card-m-badges">
                          <span className={`em-badge ${getBadgeClass(template.category)}`}>{template.category}</span>
                          {template.isActive ? (
                            <span className="em-badge em-badge-success">Active</span>
                          ) : (
                            <span className="em-badge em-badge-neutral">Inactive</span>
                          )}
                        </div>
                      </div>
                      {template.subject && (
                        <div className="emtpl-card-m-subject">
                          <span className="em-text-muted">Subject:</span> {template.subject}
                        </div>
                      )}
                      <div className="emtpl-card-m-meta">
                        <span className="emtpl-channel">{channelsList.length > 0 ? channelsList.join(', ') : 'email'}</span>
                        <span className="emtpl-date">{formatDate(template.updatedAt)}</span>
                      </div>
                      <div className="emtpl-card-m-actions">
                        <button className="em-btn em-btn-secondary em-btn-sm" onClick={() => handlePreview(template)}>Preview</button>
                        {canWriteTemplates && <button className="em-btn em-btn-primary em-btn-sm" onClick={() => handleEdit(template)}>Edit</button>}
                        {canWriteTemplates && <button className="em-btn em-btn-secondary em-btn-sm" onClick={() => handleDuplicate(template)}>Duplicate</button>}
                        {canWriteTemplates && <button className="em-btn em-btn-danger em-btn-sm" onClick={() => handleDeleteClick(template)}>Delete</button>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="em-pagination" style={{ marginTop: '1rem' }}>
                  <span className="em-pagination-info">
                    Page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages}</strong>
                  </span>
                  <div className="em-pagination-btns">
                    <button className="em-page-btn" onClick={() => handlePageChange(pagination.page - 1)} disabled={!pagination.hasPrev} aria-label="Previous page">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <button className="em-page-btn" onClick={() => handlePageChange(pagination.page + 1)} disabled={!pagination.hasNext} aria-label="Next page">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirm.open && (
            <div className="em-modal-overlay" onClick={handleDeleteCancel}>
              <div className="em-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
                <div className="em-modal-body" style={{ textAlign: 'center' }}>
                  <div className="emtpl-delete-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                  <h3 className="emtpl-delete-title">Delete Template</h3>
                  <p className="emtpl-delete-msg">
                    Are you sure you want to delete <strong>{deleteConfirm.template?.templateName}</strong>? This action cannot be undone.
                  </p>
                </div>
                <div className="em-modal-footer" style={{ justifyContent: 'center' }}>
                  <button className="em-btn em-btn-secondary" onClick={handleDeleteCancel}>Cancel</button>
                  <button className="em-btn em-btn-danger" onClick={handleDeleteConfirm}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {isPreviewOpen && previewData && (
        <TemplatePreview
          previewData={previewData}
          onClose={() => { setIsPreviewOpen(false); setPreviewData(null); }}
        />
      )}
    </div>
  );
};

export default EmailTemplates;
